import { getFirstSelection } from '../../../../bridge/utils/selectionUtils/tableSelectionAccessors';
import { isSelectingSingleSelection } from '../../../../bridge/utils/selectionUtils/tableSelectionPredicateUtils';
import type { MNHotInstance } from '../../../../bridge/tableHotInstanceTypes';
import { isFormula } from '../../../../bridge/utils/dataUtils/formula/tableFormulaPredicateUtils';
import { FORMULA_AUTOCOMPLETE_PLUGIN_NAME } from '../../../plugins/FormulaAutocompleteHotPlugin';
import type FormulaAutocompleteHotPlugin from '../../../plugins/FormulaAutocompleteHotPlugin';
import type DraftJSFormulaAutocompleteHotPlugin from '../../../plugins/DraftJSFormulaAutocompleteHotPlugin';
import { DRAFTJS_FORMULA_AUTOCOMPLETE_PLUGIN_NAME } from '../../../plugins/DraftJSFormulaAutocompleteHotPlugin';
import type { CellSelection } from '../../../../../../../common/table/TableTypes';
import { MilanoteApplicationMode } from '../../../../../../../common/platform/platformTypes';

const FORMULA_TRIGGER = '=';

type TableFormulaEditor = NonNullable<ReturnType<MNHotInstance['getMNEditor']>>;

type FormulaEditingResult = {
    cellContent: string;
    shouldOpenFormulaPicker: boolean;
};

const isEnabledFormulaAutocompletePlugin = (
    plugin: DraftJSFormulaAutocompleteHotPlugin | FormulaAutocompleteHotPlugin | undefined,
): boolean => !!plugin && (!plugin.isEnabled || plugin.isEnabled());

const isMobileMode = (hotInstance: MNHotInstance): boolean =>
    hotInstance.milanoteProps?.appMode === MilanoteApplicationMode.mobile;

// TODO: This can be removed and inlined once DraftJSFormulaAutocompleteHotPlugin is removed
const getFormulaAutocompletePlugin = (
    hotInstance: MNHotInstance,
): DraftJSFormulaAutocompleteHotPlugin | FormulaAutocompleteHotPlugin | undefined => {
    const plugins = [
        hotInstance.getPlugin(FORMULA_AUTOCOMPLETE_PLUGIN_NAME),
        hotInstance.getPlugin(DRAFTJS_FORMULA_AUTOCOMPLETE_PLUGIN_NAME),
    ] as unknown as Array<DraftJSFormulaAutocompleteHotPlugin | FormulaAutocompleteHotPlugin | undefined>;

    return plugins.find(isEnabledFormulaAutocompletePlugin);
};

/**
 * Starts formula editing from a selected cell.
 */
const beginFormulaEditing = (
    hotInstance: MNHotInstance,
    activeEditor: TableFormulaEditor,
    cellSelection: CellSelection,
    preventInitialFocus: boolean,
): FormulaEditingResult => {
    hotInstance.selectCell(...cellSelection, false, true);

    const [row, col] = cellSelection;
    const cellContent = hotInstance.getSourceDataAtCell(row, col)?.toString() || '';

    // If cell is already a formula, preserve the content of the cell and start editing
    if (isFormula(cellContent)) {
        activeEditor.beginEditing(undefined, undefined, { preventInitialFocus });
        return { cellContent, shouldOpenFormulaPicker: preventInitialFocus };
    }

    activeEditor.beginEditing(FORMULA_TRIGGER, undefined, { preventInitialFocus });
    return { cellContent: FORMULA_TRIGGER, shouldOpenFormulaPicker: true };
};

/**
 * Prepares and focuses an already open editor for formula autocomplete.
 */
const continueEditingForFormula = (
    activeEditor: TableFormulaEditor,
    preventInitialFocus: boolean,
): FormulaEditingResult => {
    const cellContent = activeEditor.getPlainTextContent();

    // If cell is already a formula, preserve the content and open the picker so
    // another formula can be added to the existing equation at the caret
    if (isFormula(cellContent)) {
        if (!preventInitialFocus) activeEditor.focus();
        return { cellContent, shouldOpenFormulaPicker: true };
    }

    // If cell is not a formula, replace cell content with `=`
    activeEditor.setPlainTextContent(FORMULA_TRIGGER);
    if (!preventInitialFocus) activeEditor.focus();
    return { cellContent: FORMULA_TRIGGER, shouldOpenFormulaPicker: true };
};

/**
 * Adds a formula to the currently selected cell in the Handsontable instance.
 */
export const addHotTableFormulaToCell = (hotInstance: MNHotInstance, cellSelections = hotInstance.getSelected()) => {
    if (!cellSelections) return;

    if (!isSelectingSingleSelection(cellSelections)) return;

    const cellSelection = getFirstSelection(cellSelections);
    if (!cellSelection) return;

    requestAnimationFrame(() => {
        if (hotInstance.isDestroyed) return;

        const activeEditor = hotInstance.getMNEditor();
        if (!activeEditor) return;

        // On mobile, the formula sheet search bar is focused after the sheet finish opening,
        // so we must prevent the cell editor from focusing to ensure the keyboard does not prematurely show
        const preventInitialFocus = isMobileMode(hotInstance);

        const { cellContent, shouldOpenFormulaPicker } = activeEditor.isOpened()
            ? continueEditingForFormula(activeEditor, preventInitialFocus)
            : beginFormulaEditing(hotInstance, activeEditor, cellSelection, preventInitialFocus);

        if (shouldOpenFormulaPicker) getFormulaAutocompletePlugin(hotInstance)?.openFormulaPicker(cellContent);
    });
};
